/**
 * Mirror a cloud collection's new `settings` into its device-local linked copy.
 *
 * A cloud REST collection can have a linked local copy (localCollectionLink).
 * Whenever the cloud settings change, the copy's snapshot is rewritten so the
 * two do not drift. Extracted from `useCollections.saveCollectionFromTab` so
 * the collection editor and the Folder variables modal share ONE implementation.
 */
import { findLinkedLocalCopy } from '../services/localCollectionLink';

/**
 * @param {object} args
 * @param {string|number} args.collectionId   the cloud collection id
 * @param {object} args.collection            the in-memory collection (spread into the copy)
 * @param {object} args.nextSettings          the settings that were just saved
 * @param {Array} args.deviceLocalCollections  workspaceStore.deviceLocalCollections
 * @param {Array} args.workspaces              workspaceStore.workspaces
 * @param {Function} [args.reloadDeviceLocalCollections]
 */
export async function syncLinkedLocalCopySettings({
  collectionId,
  collection,
  nextSettings,
  deviceLocalCollections,
  workspaces,
  reloadDeviceLocalCollections,
}) {
  const linked = findLinkedLocalCopy(deviceLocalCollections || [], collectionId);
  if (!linked?._localWorkspaceId) return;
  // Lazy: the snapshot stores reach `@tauri-apps/api/*`, which must never be in
  // a static import graph of shared UI code (tauriImportsAreLazy).
  const { saveWorkspaceSnapshot, loadSnapshotById } = await import('../services/workspaceStoreRouter');
  const { saveSnapshot } = await import('../services/localWorkspaceStore');
  const snap = await loadSnapshotById(workspaces, linked._localWorkspaceId);
  const linkedWs = (workspaces || []).find((w) => String(w.id) === String(linked._localWorkspaceId));
  const updated = (snap.collections || []).map((c) =>
    String(c.cloud_collection_id) === String(collectionId) ||
    String(c.id) === String(linked.id)
      ? {
          ...c,
          ...collection,
          settings: nextSettings,
          last_synced_at: new Date().toISOString(),
        }
      : c
  );
  if (linkedWs) {
    await saveWorkspaceSnapshot(linkedWs, { collections: updated });
  } else {
    await saveSnapshot(linked._localWorkspaceId, { collections: updated });
  }
  await reloadDeviceLocalCollections?.();
}
